import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { gsap } from 'gsap';

import BgImg from "../../assets/img/website-images/Shaping-future-pharmacists.webp";

/* ─── Variants ─────────────────────────────────────────────────── */
const fadeInLeft = {
    hidden: { opacity: 0, x: -70 },
    visible: {
        opacity: 1, x: 0,
        transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] },
    },
};

const fadeInRight = {
    hidden: { opacity: 0, x: 70 },
    visible: {
        opacity: 1, x: 0,
        transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] },
    },
};

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: (delay = 0) => ({
        opacity: 1, y: 0,
        transition: { duration: 0.65, ease: 'easeOut', delay },
    }),
};

const textStagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.15, delayChildren: 0.2 } },
};

const textChild = {
    hidden: { opacity: 0, y: 22 },
    visible: {
        opacity: 1, y: 0,
        transition: { duration: 0.6, ease: 'easeOut' },
    },
};

/* ─── Main Component ───────────────────────────────────────────── */
function Aboutkids() {
    /* Left content column */
    const { ref: leftRef,  inView: leftInView  } = useInView({ threshold: 0.2, triggerOnce: true });

    /* Right image column */
    const { ref: rightRef, inView: rightInView } = useInView({ threshold: 0.2, triggerOnce: true });

    /* GSAP: floating decorative bg images */
    const bg06Ref = useRef(null);
    const bg09Ref = useRef(null);
    useEffect(() => {
        if (bg06Ref.current) {
            gsap.to(bg06Ref.current, {
                y: -20, duration: 4, ease: 'sine.inOut', repeat: -1, yoyo: true,
            });
        }
        if (bg09Ref.current) {
            gsap.to(bg09Ref.current, {
                y: 16, duration: 5, ease: 'sine.inOut', repeat: -1, yoyo: true, delay: 1,
            });
        }
    }, []);

    /* GSAP: image scale entrance */
    const imgRef = useRef(null);
    useEffect(() => {
        if (rightInView && imgRef.current) {
            gsap.fromTo(
                imgRef.current,
                { scale: 0.88, opacity: 0 },
                { scale: 1, opacity: 1, duration: 1.0, ease: 'power3.out', delay: 0.2 }
            );
        }
    }, [rightInView]);

    return (
        <section
            className="about-area about-p pt-120 pb-120 p-relative fix"
            style={{ background: '#f7f9ff' }}
        >
            {/* Floating decorative bg */}
            <div className="animations-06" ref={bg06Ref}>
                <img src="/assets/img/bg/an-img-01.png" alt="an-img-01" />
            </div>
            <div className="animations-09" ref={bg09Ref}>
                <img src="/assets/img/bg/slider_shape03.png" alt="contact-bg-an-01" />
            </div>

            <div className="container">
                <div className="row justify-content-center align-items-center">

                    {/* ── Left: Content ── */}
                    <div className="col-lg-6 col-md-12 col-sm-12">
                        <motion.div
                            ref={leftRef}
                            className="about-content s-about-content pl-15"
                            variants={fadeInRight}
                            initial="hidden"
                            animate={leftInView ? 'visible' : 'hidden'}
                        >
                            {/* Title */}
                            <div className="about-title second-title pb-25">
                                <motion.h5
                                    variants={fadeUp}
                                    custom={0.1}
                                    initial="hidden"
                                    animate={leftInView ? 'visible' : 'hidden'}
                                >
                                    <i className="fal fa-graduation-cap" />{' '}
                                    About Anjanadri Institute of Pharmacy
                                </motion.h5>
                                <motion.h2
                                    variants={fadeUp}
                                    custom={0.2}
                                    initial="hidden"
                                    animate={leftInView ? 'visible' : 'hidden'}
                                >
                                    Shaping Future Pharmacists Through Quality Education
                                </motion.h2>
                            </div>

                            {/* Paragraphs + button — staggered */}
                            <motion.div
                                variants={textStagger}
                                initial="hidden"
                                animate={leftInView ? 'visible' : 'hidden'}
                            >
                                <motion.p className="txt-clr" variants={textChild}>
                                    Anjanadri Institute of Pharmacy, located in Bengaluru, Karnataka, is a leading centre for pharmaceutical education committed to excellence, innovation, and professional development. Established in 2023 under the Shushi Aswath Foundation, the institute provides quality education supported by modern laboratories, experienced faculty, and practical training.
                                </motion.p>

                                <motion.p variants={textChild}>
                                    The institute is affiliated to Rajiv Gandhi University of Health Sciences (RGUHS), recognized by the Pharmacy Council of India (PCI), and approved by the Government of Karnataka. Anjanadri Institute of Pharmacy focuses on nurturing skilled healthcare professionals through academic excellence, research, and industry-oriented learning.
                                </motion.p>

                                <motion.div className="slider-btn mt-20" variants={textChild}>
                                    <motion.div
                                        style={{ display: 'inline-block' }}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.97 }}
                                    >
                                        <Link to="/about-us" className="btn ss-btn smoth-scroll">
                                            Read More <i className="fal fa-long-arrow-right" />
                                        </Link>
                                    </motion.div>
                                </motion.div>
                            </motion.div>
                        </motion.div>
                    </div>

                    {/* ── Right: Image ── */}
                    <div className="col-lg-6 col-md-12 col-sm-12">
                        <motion.div
                            ref={rightRef}
                            className="s-about-img2 p-relative"
                            variants={fadeInLeft}
                            initial="hidden"
                            animate={rightInView ? 'visible' : 'hidden'}
                        >
                            <motion.img
                                ref={imgRef}
                                src={BgImg}
                                alt="About Anjanadri Institute of Pharmacy"
                                style={{ opacity: 0 }}
                                whileHover={{ scale: 1.03 }}
                                transition={{ duration: 0.4 }}
                            />
                        </motion.div>
                    </div>

                </div>
            </div>
        </section>
    );
}

export default Aboutkids;
